// frontend/src/pages/Dashboard/Admin/ManageUsers.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../utils/axiosConfig';
import { Link } from 'react-router-dom';

const ManageUsers = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // neu erstellen
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('Reader');

  // bearbeiten
  const [editUserId, setEditUserId] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/users');
      setUsers(data);
    } catch {
      setError('Fehler: Benutzer konnten nicht geladen werden.');
    }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newUsername || !newEmail) {
      setError('Benutzername & E-Mail sind erforderlich.');
      return;
    }
    try {
      await axiosInstance.post('/admin/users', {
        username: newUsername,
        email: newEmail,
        role: newRole
      });
      setSuccess('Benutzer erstellt & Zugangsdaten per E-Mail verschickt');
      setNewUsername('');
      setNewEmail('');
      setNewRole('Reader');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler: Benutzer konnte nicht erstellt werden.');
    }
  };

  const startEdit = (u) => {
    setEditUserId(u._id);
    setEditUsername(u.username);
    setEditEmail(u.email);
    setEditRole(u.role);
    setError('');
    setSuccess('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editUsername || !editEmail) {
      setError('Benutzername & E-Mail sind erforderlich.');
      return;
    }
    try {
      await axiosInstance.put(`/admin/users/${editUserId}`, {
        username: editUsername,
        email: editEmail,
        role: editRole
      });
      setSuccess('Benutzer aktualisiert');
      setEditUserId(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler: Benutzer konnte nicht aktualisiert werden.');
    }
  };

  const cancelEdit = () => {
    setEditUserId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) return;
    try {
      await axiosInstance.delete(`/admin/users/${id}`);
      setSuccess('Benutzer gelöscht');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler: Benutzer konnte nicht gelöscht werden.');
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* SIDEBAR */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} sm:block bg-gray-100 w-64 p-4`}>
        {/* Close button (mobile) */}
        <button onClick={() => setSidebarOpen(false)} className="sm:hidden mb-4">
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>

        {/* Zurück-Button */}
        <button
          onClick={() => navigate(-1)}
          className="bg-primary text-white w-full text-left px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          ← Zurück
        </button>

        {/* Admin Links */}
        <Link
          to="/admin/dashboard"
          className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          Admin-Dashboard
        </Link>
        <Link
          to="/admin/manage-articles"
          className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          Artikel verwalten
        </Link>
        <Link
          to="/admin/manage-users"
          className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          Benutzer verwalten
        </Link>
        <Link
          to="/admin/manage-comments"
          className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          Kommentare verwalten
        </Link>
        <Link
          to="/admin/manage-categories"
          className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600"
        >
          Kategorien verwalten
        </Link>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 p-4">
        {/* Menü öffnen (mobil) */}
        <button onClick={() => setSidebarOpen(true)} className="sm:hidden mb-4">
          <Bars3Icon className="w-6 h-6 text-gray-600" />
        </button>

        <motion.div className="py-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h1 className="text-3xl font-heading text-primary mb-6">Benutzer verwalten</h1>

          {error && <div className="bg-red-100 text-red-700 p-2 mb-4">{error}</div>}
          {success && <div className="bg-green-100 text-green-700 p-2 mb-4">{success}</div>}

          {/* Benutzer erstellen */}
          <form onSubmit={handleCreate} className="mb-6 flex flex-wrap items-end space-x-2">
            <div className="mb-2">
              <label className="block text-sm font-semibold">Benutzername</label>
              <input
                type="text"
                className="border p-2 rounded w-full"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-semibold">E-Mail</label>
              <input
                type="email"
                className="border p-2 rounded w-full"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-semibold">Rolle</label>
              <select
                className="border p-2 rounded w-full"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                <option value="Admin">Admin</option>
                <option value="Editor">Editor</option>
                <option value="Author">Author</option>
                <option value="Reader">Reader</option>
              </select>
            </div>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Benutzer erstellen
            </button>
          </form>

          {loading ? (
            <div className="flex justify-center">
              <div className="loader" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white text-center">
                <thead>
                  <tr>
                    <th className="border py-2 px-4">Benutzername</th>
                    <th className="border py-2 px-4">E-Mail</th>
                    <th className="border py-2 px-4">Rolle</th>
                    <th className="border py-2 px-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td className="border py-2 px-4">
                        {editUserId === u._id ? (
                          <input
                            type="text"
                            className="border p-1"
                            value={editUsername}
                            onChange={(e) => setEditUsername(e.target.value)}
                          />
                        ) : (
                          u.username
                        )}
                      </td>
                      <td className="border py-2 px-4">
                        {editUserId === u._id ? (
                          <input
                            type="email"
                            className="border p-1"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                          />
                        ) : (
                          u.email
                        )}
                      </td>
                      <td className="border py-2 px-4 capitalize">
                        {editUserId === u._id ? (
                          <select
                            className="border p-1"
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                          >
                            <option value="Admin">Admin</option>
                            <option value="Editor">Editor</option>
                            <option value="Author">Author</option>
                            <option value="Reader">Reader</option>
                          </select>
                        ) : (
                          u.role
                        )}
                      </td>
                      <td className="border py-2 px-4">
                        {editUserId === u._id ? (
                          <>
                            <button
                              onClick={handleUpdate}
                              className="bg-blue-500 text-white px-3 py-1 rounded mr-2"
                            >
                              Speichern
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="bg-gray-500 text-white px-3 py-1 rounded"
                            >
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(u)}
                              className="bg-blue-500 text-white px-3 py-1 rounded mr-2"
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => handleDelete(u._id)}
                              className="bg-red-500 text-white px-3 py-1 rounded"
                            >
                              Löschen
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ManageUsers;
